/**
 * The incoming-garbage queue.
 *
 * The engine owns the *mechanics*: batches wait, an outgoing attack cancels
 * them before any of it lands, and what survives is pushed in from the bottom.
 * The server owns the *policy* — who sends what, in what order, with how much
 * delay (BASELINE 6, and `docs/DESIGN.md` says so in as many words). Nothing
 * here decides that; every entry's delay is supplied by the caller.
 */
import { pushGarbageRow } from './board.js';
import { collides } from './piece.js';
import { nextInt } from './rng.js';
import type { GameState, GarbageEntry } from './types.js';

/**
 * Chance in ten that a garbage batch moves its hole to a new column.
 *
 * The prototype re-rolls with probability 0.3; ten is the denominator so the
 * draw stays an integer.
 */
const HOLE_RESHUFFLE_IN_TEN = 3;

/** Total lines of garbage waiting to land. */
export function pendingGarbageLines(state: GameState): number {
  let total = 0;
  for (const entry of state.pendingGarbage) total += entry.lines;
  return total;
}

/**
 * Queue a batch of incoming garbage.
 *
 * `delayTicks` is how long the target has to cancel it. The engine has no
 * opinion on that number: `docs/DESIGN.md` calls it "a short delay" and leaves
 * it to the mode and the server.
 */
export function queueGarbage(state: GameState, lines: number, delayTicks = 0): GarbageEntry | null {
  if (lines <= 0) return null;
  const entry: GarbageEntry = {
    id: state.nextGarbageId,
    lines,
    readyAtTick: state.tick + Math.max(0, delayTicks),
  };
  state.nextGarbageId += 1;
  state.pendingGarbage.push(entry);
  return entry;
}

/**
 * Spend outgoing attack on pending garbage, oldest batch first.
 *
 * Returns how many lines were cancelled. Whatever the attack does not cancel is
 * what the opponent receives.
 */
export function cancelPendingGarbage(state: GameState, lines: number): number {
  let remaining = Math.max(0, lines);
  let cancelled = 0;
  const kept: GarbageEntry[] = [];

  for (const entry of state.pendingGarbage) {
    if (remaining <= 0) {
      kept.push(entry);
      continue;
    }
    const taken = Math.min(remaining, entry.lines);
    entry.lines -= taken;
    remaining -= taken;
    cancelled += taken;
    if (entry.lines > 0) kept.push(entry);
  }

  state.pendingGarbage = kept;
  return cancelled;
}

/**
 * Carry every active piece up one row with the stack that just rose under it.
 *
 * The stack moved up one, so a piece that moves up one is exactly where it was
 * relative to everything on the board: lifting cannot put it inside the stack.
 * Two things can stop a lift. The ceiling can, and so can another piece that
 * has not been lifted yet, which is why this is not one pass in slot order: a
 * piece resting on its partner has to wait for the partner above it to go
 * first. So the passes repeat until one of them lifts nothing. Each piece
 * rises at most once, and which pieces rise does not depend on the order they
 * are visited in, because a lift never takes away another piece's room.
 *
 * A piece that could not be lifted and is now inside the stack, or inside a
 * partner that could not move either, has nowhere to be: the well is full to
 * the ceiling, and the game tops out, as it does when a clear leaves a piece
 * with no room (`lock.ts`).
 */
function liftPiecesWithStack(state: GameState): void {
  const waiting: number[] = [];
  for (const [slot, piece] of state.pieces.entries()) {
    if (piece !== null) waiting.push(slot);
  }

  let lifted = true;
  while (lifted && waiting.length > 0) {
    lifted = false;
    for (let index = 0; index < waiting.length; index++) {
      const slot = waiting[index];
      const piece = slot === undefined ? null : (state.pieces[slot] ?? null);
      if (slot === undefined || piece === null) continue;
      if (collides(state, piece.kind, piece.rotation, piece.x, piece.y - 1, slot)) continue;
      piece.y -= 1;
      waiting.splice(index, 1);
      index -= 1;
      lifted = true;
    }
  }

  for (const slot of waiting) {
    const piece = state.pieces[slot] ?? null;
    if (piece === null) continue;
    if (collides(state, piece.kind, piece.rotation, piece.x, piece.y, slot)) state.topOut = true;
  }
}

/**
 * Push `lines` garbage rows in at the bottom, raising the stack.
 *
 * The hole moves to a new column now and then, drawn from the seeded PRNG so a
 * replay lands the holes in the same places. The active pieces rise with the
 * stack; see `liftPiecesWithStack` for the one case in which a piece cannot.
 */
export function applyGarbageLines(state: GameState, lines: number): number {
  let applied = 0;
  for (let i = 0; i < lines; i++) {
    if (nextInt(state.rng, 10) < HOLE_RESHUFFLE_IN_TEN) {
      state.garbageColumn = nextInt(state.rng, state.config.width);
    }
    if (pushGarbageRow(state.board, state.garbageColumn)) state.topOut = true;
    liftPiecesWithStack(state);
    applied += 1;
  }
  return applied;
}

/** Land every batch whose delay has run out. Called once per tick. */
export function releaseDueGarbage(state: GameState): number {
  let applied = 0;
  const kept: GarbageEntry[] = [];

  for (const entry of state.pendingGarbage) {
    if (entry.readyAtTick > state.tick) {
      kept.push(entry);
      continue;
    }
    applied += applyGarbageLines(state, entry.lines);
  }

  state.pendingGarbage = kept;
  return applied;
}
